import { computed, effect, inject, Injectable, signal } from '@angular/core';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { FeatureFlagClient } from 'src/httpClient/luminav-link-api';
import { AuthService } from 'src/app/services/auth.service';

/** Known feature-flag keys — must match the backend FeatureFlag.Key values. */
export const FEATURE_FLAGS = {
  PatientProfileRevamp: 'patient-profile-revamp',
  PatientConsent: 'patient-consent',
} as const;

/**
 * Loads the global feature flags once at app bootstrap and exposes them as signals.
 * Fail-safe by design: if the flags can't be loaded, every flag reads OFF (the old,
 * proven UI shows and half-built features stay hidden).
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly client = inject(FeatureFlagClient);
  private readonly authService = inject(AuthService);

  /** Shared in-flight load, so a concurrent effect + route guard don't each fire a GET. */
  private inFlight: Observable<void> | null = null;

  constructor() {
    // Warm the flags as soon as the user is authenticated (the /features endpoint requires auth, and
    // APP_INITIALIZER runs before auth completes). Route guards also call ensureLoaded(), so a deep
    // link that beats this effect still waits for the same load.
    effect(() => {
      const user = this.authService.currentUser();
      if (user) {
        this.ensureLoaded().subscribe();
      }
    });
  }

  /** The set of enabled flag keys. Empty until loaded; empty again on any load failure. */
  private readonly enabledKeys = signal<ReadonlySet<string>>(new Set<string>());

  /** True once a load attempt has completed (success or fail-safe), so guards can wait. */
  public readonly loaded = signal<boolean>(false);

  public readonly revampEnabled = computed(() =>
    this.enabledKeys().has(FEATURE_FLAGS.PatientProfileRevamp),
  );
  public readonly consentEnabled = computed(() =>
    this.enabledKeys().has(FEATURE_FLAGS.PatientConsent),
  );

  /** Whether an arbitrary flag key is enabled. */
  public isEnabled(key: string): boolean {
    return this.enabledKeys().has(key);
  }

  /**
   * Loads all flags. Never throws — a failure leaves every flag OFF. Call once at bootstrap
   * (after auth) and again whenever a refresh is warranted.
   */
  public load(): Observable<void> {
    return this.client.getFeatureFlags().pipe(
      tap((res) => {
        const on = new Set<string>(
          (res.flags ?? [])
            // Gate on the server-computed effective state (isCurrentlyEnabled), which honours a
            // flag's schedule window. Never compute the window client-side — the browser clock
            // and server clock can disagree.
            .filter((f) => f.isCurrentlyEnabled && f.key)
            .map((f) => f.key as string),
        );
        this.enabledKeys.set(on);
        this.loaded.set(true);
      }),
      map(() => undefined),
      catchError(() => {
        // Fail-safe: on error every flag is OFF.
        this.enabledKeys.set(new Set<string>());
        this.loaded.set(true);
        return of(undefined);
      }),
    );
  }

  /**
   * Resolves once the flags have loaded, loading them if needed. Route guards await this before
   * deciding which patient view to show, so a deep link / refresh never decides on an empty set.
   * Idempotent and de-duplicated: concurrent callers share one in-flight load, and once loaded it
   * resolves synchronously. Never throws — a failed load leaves every flag OFF (fail-safe).
   */
  public ensureLoaded(): Observable<void> {
    if (this.loaded()) {
      return of(undefined);
    }
    if (!this.inFlight) {
      this.inFlight = this.load().pipe(
        finalize(() => (this.inFlight = null)),
        shareReplay(1),
      );
    }
    return this.inFlight;
  }

  /**
   * Reflects a toggle locally so the admin who just flipped it sees the change immediately
   * (other sessions pick it up on their next load).
   */
  public setLocal(key: string, isEnabled: boolean): void {
    const next = new Set(this.enabledKeys());
    if (isEnabled) {
      next.add(key);
    } else {
      next.delete(key);
    }
    this.enabledKeys.set(next);
  }
}
